import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Gauge,
  GitCompareArrows,
  ListChecks,
  ShieldCheck,
  ShieldX,
  Timer,
  TrendingUp,
} from "lucide-react";
import type { Lead, LeadQuote } from "@ir/types";
import {
  deriveMarketplacePricingSummary,
  formatDateTime,
  formatNumber,
  formatPercent,
  fullName,
  getOverview,
  latestQuoteActivityAt,
  searchLeads,
  searchQuotes,
} from "@ir/services";
import { Alert, Card, EmptyState, PageHeader, staggerChildren } from "@ir/ui";
import { useAuth } from "../auth/AuthProvider";
import { useAsyncData } from "../hooks/useAsyncData";
import { NAV_SECTIONS } from "../layout/navigation";

const RECENT_QUOTES_LIMIT = 5;
const RECENT_LEADS_LIMIT = 5;

const PROVIDER_LABELS = {
  granite: "Granite",
  inshur: "INSHUR",
  kinetiq: "Kinetiq",
} as const;

type ProviderKey = keyof typeof PROVIDER_LABELS;

export function OverviewPage() {
  const { user, can } = useAuth();

  const overview = useAsyncData((signal) => getOverview(signal), []);
  const recentQuotes = useAsyncData(
    (signal) =>
      searchQuotes({
        pageNumber: 1,
        pageSize: RECENT_QUOTES_LIMIT,
        signal,
      }),
    [],
  );
  const recentLeads = useAsyncData(
    (signal) =>
      searchLeads({
        pageNumber: 1,
        pageSize: RECENT_LEADS_LIMIT,
        signal,
      }),
    [],
  );

  const shortcuts = NAV_SECTIONS.flatMap((section) => section.items).filter(
    (item) => !item.end && (!item.permission || can(item.permission)),
  );
  const firstName = user?.name?.split(" ")[0] ?? "there";

  const providerQuoteCounts = overview.data?.providerQuoteCounts ?? {
    granite: 0,
    inshur: 0,
    kinetiq: 0,
  };

  const stats = {
    quotesToday: overview.data?.quotesToday ?? 0,
    quotesTotal: overview.data?.quotesTotal ?? recentQuotes.data?.totalCount ?? 0,
    leadsToday: overview.data?.leadsToday ?? 0,
    leadsTotal: overview.data?.leadsTotal ?? recentLeads.data?.totalCount ?? 0,
    quotedCount: overview.data?.quotedCount ?? 0,
    partialCount: overview.data?.partialCount ?? 0,
    failedCount: overview.data?.failedCount ?? 0,
    pendingCount: overview.data?.pendingCount ?? 0,
    ogiDeliveredCount: overview.data?.ogiDeliveredCount ?? 0,
    ogiQueuedCount: overview.data?.ogiQueuedCount ?? 0,
  };

  const totalQuotes = stats.quotesTotal;
  const pricedQuotes = stats.quotedCount + stats.partialCount;
  const resolvedQuotes = pricedQuotes + stats.failedCount;
  const ogiHandledCount = stats.ogiDeliveredCount + stats.ogiQueuedCount;
  const loadingOverview = overview.loading;
  const refreshingOverview =
    overview.refreshing || recentQuotes.refreshing || recentLeads.refreshing;

  const providerMetrics = (Object.keys(PROVIDER_LABELS) as ProviderKey[]).map((provider) => {
    const count = providerQuoteCounts[provider] ?? 0;
    return {
      provider,
      label: PROVIDER_LABELS[provider],
      count,
      rate: totalQuotes > 0 ? count / totalQuotes : 0,
    };
  });

  return (
    <>
      <PageHeader
        icon={Gauge}
        title={`Good to see you, ${firstName}`}
        description="A live operational read on what has come through the marketplace."
      />

      {overview.error ? (
        <Alert tone="warning" title="Overview endpoint is partially unavailable">
          {overview.error} The dashboard below still uses the live quotes and leads
          lists so the page stays useful.
        </Alert>
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Quotes today"
          value={formatNumber(stats.quotesToday, "0")}
          hint="since midnight"
          icon={ClipboardList}
          loading={loadingOverview}
        />
        <KpiCard
          label="Leads today"
          value={formatNumber(stats.leadsToday, "0")}
          hint="raw form submissions"
          icon={ListChecks}
          loading={loadingOverview}
        />
        <KpiCard
          label="Fully quoted"
          value={formatNumber(stats.quotedCount, "0")}
          hint="3 of 3 pricing providers"
          icon={CheckCircle2}
          loading={loadingOverview}
        />
        <KpiCard
          label="Partial"
          value={formatNumber(stats.partialCount, "0")}
          hint="some prices returned"
          icon={GitCompareArrows}
          loading={loadingOverview}
        />
        <KpiCard
          label="Failed"
          value={formatNumber(stats.failedCount, "0")}
          hint="resolved but with no pricing"
          icon={ShieldX}
          loading={loadingOverview}
        />
        <KpiCard
          label="Awaiting prices"
          value={formatNumber(stats.pendingCount, "0")}
          hint="still with the insurers"
          icon={Timer}
          loading={loadingOverview}
        />
        <KpiCard
          label="OGI delivered"
          value={formatNumber(stats.ogiDeliveredCount, "0")}
          hint="handoff completed"
          icon={ShieldCheck}
          loading={loadingOverview}
        />
        <KpiCard
          label="OGI queued"
          value={formatNumber(stats.ogiQueuedCount, "0")}
          hint="awaiting broker delivery"
          icon={Timer}
          loading={loadingOverview}
        />
      </div>

      <div className="mt-8 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
        <Card>
          <SectionHeader
            icon={TrendingUp}
            title="Marketplace performance"
            description="All-time volume and resolution across this environment"
          />

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricBlock
              label="Quotes"
              value={formatNumber(stats.quotesTotal, "0")}
              hint="stored quote records"
            />
            <MetricBlock
              label="Leads"
              value={formatNumber(stats.leadsTotal, "0")}
              hint="raw form submissions"
            />
            <MetricBlock
              label="Pricing hit rate"
              value={formatPercent(totalQuotes > 0 ? pricedQuotes / totalQuotes : 0, "0%")}
              hint={`${formatNumber(pricedQuotes, "0")} with customer-facing prices`}
            />
            <MetricBlock
              label="Resolved"
              value={formatPercent(totalQuotes > 0 ? resolvedQuotes / totalQuotes : 0, "0%")}
              hint={`${formatNumber(resolvedQuotes, "0")} no longer awaiting providers`}
            />
          </div>

          <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <OutcomeTile
              label="Quoted"
              value={stats.quotedCount}
              total={stats.quotesTotal}
              tone="success"
            />
            <OutcomeTile
              label="Partial"
              value={stats.partialCount}
              total={stats.quotesTotal}
              tone="warning"
            />
            <OutcomeTile
              label="Failed"
              value={stats.failedCount}
              total={stats.quotesTotal}
              tone="danger"
            />
            <OutcomeTile
              label="Pending"
              value={stats.pendingCount}
              total={stats.quotesTotal}
              tone="neutral"
            />
          </div>
        </Card>

        <Card>
          <SectionHeader
            icon={ShieldCheck}
            title="OpenGI handoff"
            description="Delivery state of the OGI broker handoff"
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <MetricBlock
              label="Delivered"
              value={formatNumber(stats.ogiDeliveredCount, "0")}
              hint={formatPercent(
                totalQuotes > 0 ? stats.ogiDeliveredCount / totalQuotes : 0,
                "0%",
              )}
            />
            <MetricBlock
              label="Queued"
              value={formatNumber(stats.ogiQueuedCount, "0")}
              hint={formatPercent(
                totalQuotes > 0 ? stats.ogiQueuedCount / totalQuotes : 0,
                "0%",
              )}
            />
            <MetricBlock
              label="Any handoff"
              value={formatNumber(ogiHandledCount, "0")}
              hint={`${formatPercent(
                totalQuotes > 0 ? ogiHandledCount / totalQuotes : 0,
                "0%",
              )} of all quotes`}
            />
          </div>

          <div className="mt-5 rounded-2xl border border-line bg-surface-sunken p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
              Operational note
            </p>
            <ul className="mt-3 space-y-2 text-sm text-content-muted">
              <li>Delivered means OGI accepted the broker handoff immediately.</li>
              <li>Queued means OGI accepted the quote reference but broker delivery is still pending.</li>
              <li>Customer-facing pricing still comes only from Granite, INSHUR and Kinetiq.</li>
            </ul>
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <SectionHeader
            icon={TrendingUp}
            title="Provider pricing reach"
            description="How often each pricing provider returned customer-facing prices"
          />

          <div className="space-y-3">
            {providerMetrics.map((metric) => (
              <ProviderReachRow
                key={metric.provider}
                label={metric.label}
                count={metric.count}
                rate={metric.rate}
              />
            ))}
          </div>
        </Card>

        <Card>
          <SectionHeader
            icon={CheckCircle2}
            title="Outcome mix"
            description="Quote status split across the marketplace flow"
          />

          <div className="space-y-3">
            <OutcomeRow label="Fully quoted" value={stats.quotedCount} total={stats.quotesTotal} />
            <OutcomeRow label="Partial" value={stats.partialCount} total={stats.quotesTotal} />
            <OutcomeRow label="Failed" value={stats.failedCount} total={stats.quotesTotal} />
            <OutcomeRow label="Pending" value={stats.pendingCount} total={stats.quotesTotal} />
          </div>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <Card>
          <SectionHeader
            icon={ClipboardList}
            title="Recent quote activity"
            description="Most recent quote records and their latest provider activity"
          />

          <div className="space-y-3">
            {recentQuotes.data?.items?.length ? (
              recentQuotes.data.items.map((quote) => (
                <RecentQuoteRow key={quote.id} quote={quote} />
              ))
            ) : (
              <EmptyState
                title="No quotes yet"
                description="Quote activity will appear here as soon as submissions are processed."
                className="py-8"
              />
            )}
          </div>
        </Card>

        <Card>
          <SectionHeader
            icon={ListChecks}
            title="Recent lead submissions"
            description="Latest raw submissions captured before quote mapping"
          />

          <div className="space-y-3">
            {recentLeads.data?.items?.length ? (
              recentLeads.data.items.map((lead) => (
                <RecentLeadRow key={lead.id} lead={lead} />
              ))
            ) : (
              <EmptyState
                title="No leads yet"
                description="New raw form submissions will appear here."
                className="py-8"
              />
            )}
          </div>
        </Card>
      </div>

      <motion.div
        variants={staggerChildren(0.06, 0.1)}
        initial="hidden"
        animate="visible"
        className="mt-8"
      >
        <div className="mb-3 flex items-center gap-3">
          <h2 className="font-display text-sm font-semibold uppercase tracking-[0.1em] text-content-subtle">
            Jump to
          </h2>
          <span aria-hidden="true" className="h-px flex-1 rule-gold" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {shortcuts.map((item) => (
            <motion.div
              key={item.to}
              variants={{
                hidden: { opacity: 0, y: 10 },
                visible: { opacity: 1, y: 0 },
              }}
            >
              <Link to={item.to} className="block h-full focus-visible:outline-none">
                <Card interactive className="group h-full">
                  <div className="flex items-start gap-3">
                    <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-gold-100 text-gold-700 transition-colors group-hover:bg-gold-200 dark:bg-gold-950/60 dark:text-gold-400 dark:group-hover:bg-gold-900/70">
                      <item.icon size={18} aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3 className="flex items-center gap-1.5 text-sm font-semibold text-content">
                        {item.label}
                        <ArrowRight
                          size={14}
                          aria-hidden="true"
                          className="opacity-0 transition-all duration-200 group-hover:translate-x-0.5 group-hover:opacity-100"
                        />
                      </h3>
                      <p className="mt-0.5 text-xs text-content-muted">
                        {item.description}
                      </p>
                    </div>
                  </div>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {refreshingOverview ? (
        <p className="mt-4 text-xs text-content-subtle">Refreshing live marketplace stats…</p>
      ) : null}
    </>
  );
}

function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  loading,
}: {
  label: string;
  value: string;
  hint: string;
  icon: typeof Gauge;
  loading: boolean;
}) {
  return (
    <Card className="p-0">
      <div className="flex items-start justify-between gap-4 p-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
            {label}
          </p>
          <p className="mt-3 text-3xl font-semibold tabular text-content">
            {loading ? "—" : value}
          </p>
          <p className="mt-2 text-sm text-content-muted">{hint}</p>
        </div>
        <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
          <Icon size={18} aria-hidden="true" />
        </span>
      </div>
    </Card>
  );
}

function SectionHeader({
  icon: Icon,
  title,
  description,
}: {
  icon: typeof Gauge;
  title: string;
  description: string;
}) {
  return (
    <div className="mb-4 flex items-center gap-3">
      <span className="grid size-9 place-items-center rounded-lg bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
        <Icon size={17} aria-hidden="true" />
      </span>
      <div>
        <p className="text-sm font-medium text-content">{title}</p>
        <p className="text-xs text-content-subtle">{description}</p>
      </div>
    </div>
  );
}

function MetricBlock({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="rounded-2xl border border-line bg-surface-sunken px-4 py-3">
      <p className="text-xs uppercase tracking-[0.08em] text-content-subtle">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular text-content">{value}</p>
      <p className="mt-1 text-xs text-content-muted">{hint}</p>
    </div>
  );
}

function OutcomeTile({
  label,
  value,
  total,
  tone,
}: {
  label: string;
  value: number;
  total: number;
  tone: "success" | "warning" | "danger" | "neutral";
}) {
  const toneClasses = {
    success: "border-success-500/20 bg-success-50/60 dark:bg-success-500/10",
    warning: "border-warning-500/20 bg-warning-50/60 dark:bg-warning-500/10",
    danger: "border-danger-500/20 bg-danger-50/60 dark:bg-danger-500/10",
    neutral: "border-line bg-surface-sunken",
  } as const;

  return (
    <div className={`rounded-2xl border px-4 py-3 ${toneClasses[tone]}`}>
      <p className="text-sm font-medium text-content">{label}</p>
      <p className="mt-2 text-2xl font-semibold tabular text-content">
        {formatNumber(value, "0")}
      </p>
      <p className="mt-1 text-xs text-content-muted">
        {formatPercent(total > 0 ? value / total : 0, "0%")} of all quotes
      </p>
    </div>
  );
}

function ProviderReachRow({
  label,
  count,
  rate,
}: {
  label: string;
  count: number;
  rate: number;
}) {
  return (
    <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3">
      <div className="flex items-center justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-content">{label}</p>
          <p className="text-xs text-content-subtle">
            {formatPercent(rate, "0%")} quote return rate
          </p>
        </div>
        <p className="text-lg font-semibold tabular text-content">
          {formatNumber(count, "0")}
        </p>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-line">
        <div
          className="h-full rounded-full bg-gold-sheen"
          style={{ width: `${Math.max(0, Math.min(100, rate * 100))}%` }}
        />
      </div>
    </div>
  );
}

function OutcomeRow({
  label,
  value,
  total,
}: {
  label: string;
  value: number;
  total: number;
}) {
  const percentage = total > 0 ? value / total : 0;

  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-line bg-surface-sunken px-4 py-3">
      <div>
        <p className="text-sm font-medium text-content">{label}</p>
        <p className="text-xs text-content-subtle">
          {formatPercent(percentage, "0%")} of all quotes
        </p>
      </div>
      <p className="text-lg font-semibold tabular text-content">
        {formatNumber(value, "0")}
      </p>
    </div>
  );
}

function RecentQuoteRow({ quote }: { quote: LeadQuote }) {
  const driver = quote.driver?.[0];
  const summary = deriveMarketplacePricingSummary(quote.base_quotes ?? []);

  return (
    <Link to={`/quotes/${quote.id}`} className="block focus-visible:outline-none">
      <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3 transition-colors hover:border-gold-300">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-content">
              {fullName([driver?.title, driver?.first_name, driver?.last_name])}
            </p>
            <p className="mt-0.5 truncate text-xs text-content-muted">
              {quote.type ?? "Unknown type"} • {quote.id}
            </p>
          </div>
          <span className="rounded-full border border-line px-2.5 py-1 text-xs font-medium text-content">
            {summary.overallStatus}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-content-subtle">
          <span>
            Pricing: {summary.quotedProviderCount}/{summary.expectedProviderCount}
          </span>
          <span>OGI: {summary.ogiSentSuccessfully ? "Delivered" : summary.ogiStatus ?? "No send"}</span>
          <span>Updated: {formatDateTime(latestQuoteActivityAt(quote))}</span>
        </div>
      </div>
    </Link>
  );
}

function RecentLeadRow({ lead }: { lead: Lead }) {
  return (
    <Link to={`/leads/${lead.id}`} className="block focus-visible:outline-none">
      <div className="rounded-xl border border-line bg-surface-sunken px-4 py-3 transition-colors hover:border-gold-300">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-content">
              {fullName([lead.first_name, lead.surname])}
            </p>
            <p className="mt-0.5 truncate text-xs text-content-muted">
              {lead.email ?? lead.mobile ?? "No contact"} • {lead.type ?? "Unknown type"}
            </p>
          </div>
          <span className="rounded-full border border-line px-2.5 py-1 text-xs font-medium text-content">
            {lead.partial ? "Partial" : "Complete"}
          </span>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-content-subtle">
          <span>{lead.mobile ?? "No mobile"}</span>
          <span>{lead.info?.postcode ?? "No postcode"}</span>
          <span>Created: {formatDateTime(lead.created_at ?? lead.timestamp ?? lead._ts)}</span>
        </div>
      </div>
    </Link>
  );
}
